import type { TFunction } from 'i18next';

// Campaign-list ordering — pure so the tie-break and missing-date rules are
// unit-testable without mounting the list (which pulls the whole redux tree).
//
// Requested by a customer who schedules many campaigns per day and browses them
// by date: the list was hard-coded newest-first with no control, so reaching an
// earlier campaign meant scrolling past everything later. Complements #162 (the
// compact row view) — sorting decides WHICH rows you reach, density decides how
// many you see at once.

/** Sort keys offered in the campaigns list. */
export type CampaignSortId = 'date-desc' | 'date-asc';

// A TYPE-only import of i18next's own `TFunction`: the module stays pure (no
// react-i18next, no React runtime) but the parameter is the real thing rather
// than a narrower hand-written shape the caller's `t` would not satisfy.

/**
 * The label for a sort order, in the reader's language.
 *
 * This was a module-level `Record<CampaignSortId, string>` of English, evaluated
 * at import time where no `t` exists — so a Hebrew campaigns list rendered
 * "Newest first" next to correctly-translated controls. Same shape, same cause
 * and same fix as `primitives.tsx`'s `STATUS_MAP`/`MEDIA_META`.
 *
 * `t` is a REQUIRED first parameter rather than an optional one, so the compiler
 * finds every call site instead of a forgotten one silently rendering English.
 */
export function campaignSortLabel(t: TFunction, sort: CampaignSortId): string {
  return sort === 'date-asc'
    ? t('campaignsRedesign.sortOldestFirst', 'Oldest first')
    : t('campaignsRedesign.sortNewestFirst', 'Newest first');
}

export const DEFAULT_CAMPAIGN_SORT: CampaignSortId = 'date-desc';

/** Minimum a row must expose to be ordered. */
export interface SortableCard {
  /** Send/scheduled time (falls back to creation) — the date the row displays. */
  scheduledAtMs: number;
  /** Stable tie-break so equal dates never reorder between renders. */
  id: string;
}

/**
 * Order campaigns by date. Returns a NEW array (never mutates the input, which
 * is a memoised value shared with the count pills).
 *
 * Rows with no usable date (`0`/NaN from an absent or unparseable timestamp)
 * always sink to the bottom in BOTH directions. Ascending is "earliest first",
 * and a dateless row is not earliest — it is unknown; floating it to the top
 * would bury the row the user actually asked for behind rows with no date at all.
 */
export function sortCampaignCards<T extends SortableCard>(cards: T[], sort: CampaignSortId): T[] {
  const dir = sort === 'date-asc' ? 1 : -1;
  const usable = (ms: number) => Number.isFinite(ms) && ms > 0;
  return [...cards].sort((a, b) => {
    const aOk = usable(a.scheduledAtMs);
    const bOk = usable(b.scheduledAtMs);
    if (aOk !== bOk) return aOk ? -1 : 1; // dateless sinks, whichever direction
    if (aOk && a.scheduledAtMs !== b.scheduledAtMs) {
      return (a.scheduledAtMs - b.scheduledAtMs) * dir;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** Narrow an unknown (e.g. localStorage) value to a valid sort id. */
export function coerceCampaignSort(value: unknown): CampaignSortId {
  return value === 'date-asc' || value === 'date-desc' ? value : DEFAULT_CAMPAIGN_SORT;
}

// ─── Audience (board) filter ───────────────────────────────────────────────
// FE#339. A DEDICATED filter by audience/board, distinct from the free-text
// search box. A customer who schedules many campaigns per day across a handful
// of reused boards needs to isolate one board's campaigns — the free-text
// search only matches names/messages, not "everything sent to THIS audience",
// and typing a board name is both undiscoverable and lossy (two boards can share
// a word). Pairing this with the date sort is what lets them reach today's rows
// for a given board without scrolling past every other board's traffic.

/** Sentinel filter id: no audience filter (show every card). */
export const ALL_AUDIENCES_ID = 'all';
/** Sentinel filter id bucketing cards that target NO board at all. */
export const NO_AUDIENCE_ID = '__none__';

/** One entry in the audience filter dropdown. */
export interface AudienceOption {
  /** A real board id, or the `NO_AUDIENCE_ID` sentinel. */
  id: string;
  /** Display label (board name, or "No audience"). */
  name: string;
  /** How many currently-loaded cards this option matches. */
  count: number;
}

/** Minimum a card must expose to be audience-filtered. */
export interface AudienceFilterable {
  /** Board ids this card targets. A drip/campaign can target several (#161); an
   *  empty array means it targets no board. */
  boardIds: string[];
}

/**
 * Build the audience filter options from the loaded cards: one entry per board
 * id any card targets, labelled from `boardNameById` (falling back to 'Audience'
 * for a board whose name has not loaded yet), sorted by name with the id as a
 * stable tie-break. Cards that target no board are collected under a trailing
 * "No audience" bucket, emitted only when at least one such card exists. A card
 * targeting several boards counts once toward EACH (deduped so a card that lists
 * the same board twice still counts once).
 *
 * Pure and does not include the synthetic "All" row — the caller renders that,
 * since its count is simply `cards.length`.
 *
 * Both labels used to be module-level English literals evaluated at import time,
 * where no `t` exists — so a Hebrew campaigns list offered "Audience" and
 * "No audience" in its audience filter beside correctly-translated controls.
 * Same shape, same cause and same fix as `campaignSortLabel` above: `t` is a
 * REQUIRED first parameter, so the compiler finds every call site rather than
 * leaving a forgotten one silently rendering English.
 */
export function collectAudienceOptions<T extends AudienceFilterable>(
  t: TFunction,
  cards: readonly T[],
  boardNameById: Record<string, string | undefined>,
): AudienceOption[] {
  const counts = new Map<string, number>();
  let none = 0;
  for (const card of cards) {
    const ids = card.boardIds ?? [];
    if (ids.length === 0) {
      none += 1;
      continue;
    }
    for (const id of new Set(ids)) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const options: AudienceOption[] = Array.from(counts, ([id, count]) => ({
    id,
    name: boardNameById[id] ?? t('campaignsRedesign.audienceLabel', 'Audience'),
    count,
  }));
  options.sort(
    (a, b) => a.name.localeCompare(b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  if (none > 0) {
    options.push({ id: NO_AUDIENCE_ID, name: t('campaignsRedesign.audienceNone', 'No audience'), count: none });
  }
  return options;
}

/** Whether a card belongs to the selected audience filter. */
export function cardMatchesAudience(card: AudienceFilterable, audienceId: string): boolean {
  if (audienceId === ALL_AUDIENCES_ID) return true;
  const ids = card.boardIds ?? [];
  if (audienceId === NO_AUDIENCE_ID) return ids.length === 0;
  return ids.includes(audienceId);
}

/**
 * Narrow an arbitrary value (e.g. the `?audience=` URL param) to a currently-
 * selectable audience id. An id no loaded option matches — a board since removed
 * from every campaign, or a hand-typed param — falls back to `ALL_AUDIENCES_ID`,
 * so the list never gets stuck showing an empty, un-clearable filter.
 */
export function coerceAudienceId(value: unknown, options: readonly AudienceOption[]): string {
  return typeof value === 'string' && options.some((o) => o.id === value)
    ? value
    : ALL_AUDIENCES_ID;
}
